import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Paperclip, X } from "lucide-react";
import api from "../../lib/api";
import { formatDay, minorToText, todayIso, toMinor } from "../../lib/finance";
import {
  END_CODE_LABELS, PAYMENT_METHOD_LABELS, PERIODICITY_LABELS, REMINDER_CHANNEL_LABELS, billingError, listPrice, money, needsPriceReason, newKey,
} from "../../lib/billing";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

function useRequest() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (request, onDone, success) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await request();
      if (success) toast.success(success);
      onDone(data);
    } catch (e) {
      setError(billingError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

function Footer({ req, onCancel, onSubmit, disabled, label, danger, testid }) {
  return (
    <>
      {req.error && <p className="text-sm text-red-600" role="alert" data-testid="billing-dialog-error">{req.error}</p>}
      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={req.busy}>Fermer</Button>
        <Button variant={danger ? "destructive" : "default"} onClick={onSubmit} disabled={req.busy || disabled} data-testid={testid}>
          {req.busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{label}
        </Button>
      </DialogFooter>
    </>
  );
}

function Row({ id, label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

function DayInput({ id, value, onChange, max, testid }) {
  return <Input id={id} type="date" value={value} max={max} onChange={(e) => onChange(e.target.value)} className="h-10" data-testid={testid} />;
}

// PDF or image of an invoice, a credit note or a payment proof: a private file (context « billing_document »), attached by the write.
function FilesField({ files, setFiles }) {
  const [uploading, setUploading] = useState(false);
  const input = useRef(null);
  const upload = async (picked) => {
    if (!picked.length) return;
    setUploading(true);
    try {
      for (const file of picked) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/files", form, { params: { context: "billing_document" }, headers: { "Idempotency-Key": newKey() } });
        setFiles((list) => [...list, { id: data.id, name: data.name || file.name }]);
      }
    } catch (e) {
      toast.error(billingError(e, "Fichier refusé : PDF ou image (JPEG, PNG, WebP) de 10 Mo maximum."));
    } finally {
      setUploading(false);
      if (input.current) input.current.value = "";
    }
  };
  return (
    <div className="space-y-1.5">
      <Label>Document (PDF ou image)</Label>
      <div className="flex flex-wrap items-center gap-2">
        {files.map((f) => (
          <span key={f.id} className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs">
            <Paperclip size={12} />{f.name}
            <button type="button" aria-label={`Retirer ${f.name}`} onClick={() => setFiles((list) => list.filter((x) => x.id !== f.id))}><X size={12} /></button>
          </span>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => input.current?.click()} disabled={uploading || files.length >= 5}
          data-testid="billing-file-add">
          {uploading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Paperclip size={14} className="mr-1.5" />}Joindre
        </Button>
        <input ref={input} type="file" className="hidden" accept="application/pdf,image/jpeg,image/png,image/webp" multiple
          onChange={(e) => upload(Array.from(e.target.files || []))} />
      </div>
    </div>
  );
}

// --- A reason, nothing else: pause, cancel an installment / invoice / credit note / payment, cancel a draft ---------------------
export function ReasonDialog({ open, title, description, label = "Motif", placeholder, confirm, danger, optional, success, onSubmit, onDone, onOpenChange, testid }) {
  const req = useRequest();
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) { setReason(""); req.setError(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid={testid || "billing-reason-dialog"}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <Row id="billing-reason" label={optional ? `${label} (facultatif)` : label}>
          <Textarea id="billing-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} placeholder={placeholder}
            data-testid="billing-reason" />
        </Row>
        <Footer req={req} onCancel={() => onOpenChange(false)} label={confirm} danger={danger} testid="billing-reason-submit"
          disabled={!optional && reason.trim().length < 3}
          onSubmit={() => req.run(() => onSubmit(reason.trim()), (data) => { onOpenChange(false); onDone?.(data); }, success)} />
      </DialogContent>
    </Dialog>
  );
}

// --- Subscription transitions ---------------------------------------------------------------------------------------------
export function StartDialog({ sub, action, onOpenChange, onDone }) {
  const req = useRequest();
  const billing = action === "start_billing" || (action === "start" && !sub?.trial_end_date);
  const fallback = action === "start_billing" && sub?.trial_end_date
    ? new Date(Date.parse(`${sub.trial_end_date}T00:00:00Z`) + 86400000).toISOString().slice(0, 10) : sub?.start_date;
  const [from, setFrom] = useState(fallback || "");
  useEffect(() => { setFrom(fallback || ""); req.setError(""); }, [action, sub?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!sub || !action) return null;
  const path = action === "start" ? "start" : "start-billing";
  const submit = () => req.run(() => api.post(`/billing/subscriptions/${sub.id}/${path}`,
    { expected_version: sub.version, ...(billing && from ? { billing_from: from } : {}) }), onDone,
  billing ? "Facturation démarrée" : "Essai démarré");
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="billing-start-dialog">
        <DialogHeader>
          <DialogTitle>{action === "start_billing" ? "Démarrer la facturation" : billing ? "Démarrer l'abonnement" : "Démarrer l'essai"}</DialogTitle>
          <DialogDescription>{sub.number} · {sub.pharmacy_name}</DialogDescription>
        </DialogHeader>
        {billing ? (
          <Row id="billing-from" label="Facturer à partir du" hint="Les échéances suivent cette date chaque période (date anniversaire). Seule la période en cours est facturée, jamais les précédentes.">
            <DayInput id="billing-from" value={from} onChange={setFrom} testid="billing-from" />
          </Row>
        ) : (
          <p className="text-sm text-slate-600">Aucune échéance n'est créée pendant l'essai (jusqu'au {formatDay(sub.trial_end_date)}). La facturation démarrera quand vous le déciderez.</p>
        )}
        <Footer req={req} onCancel={() => onOpenChange(false)} onSubmit={submit} label={billing ? "Démarrer la facturation" : "Démarrer l'essai"}
          testid="billing-start-submit" />
      </DialogContent>
    </Dialog>
  );
}

export function EndDialog({ sub, onOpenChange, onDone }) {
  const req = useRequest();
  const [code, setCode] = useState("customer_termination");
  const [day, setDay] = useState(todayIso());
  const [reason, setReason] = useState("");
  if (!sub) return null;
  const submit = () => req.run(() => api.post(`/billing/subscriptions/${sub.id}/end`,
    { expected_version: sub.version, end_code: code, end_date: day, reason: reason.trim() }), onDone, "Abonnement terminé");
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="billing-end-dialog">
        <DialogHeader>
          <DialogTitle>Terminer l'abonnement</DialogTitle>
          <DialogDescription>{sub.number} · {sub.pharmacy_name}. Les échéances déjà créées restent dues ; aucune autre ne sera créée.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Row id="billing-end-code" label="Raison">
            <Select value={code} onValueChange={setCode}>
              <SelectTrigger id="billing-end-code" className="h-10" data-testid="billing-end-code"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(END_CODE_LABELS).filter(([k]) => k !== "offer_change").map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </Row>
          <Row id="billing-end-date" label="Dernier jour" hint="Le jour où la fin a lieu, jamais à l'avance.">
            <DayInput id="billing-end-date" value={day} onChange={setDay} max={todayIso()} testid="billing-end-date" />
          </Row>
          <Row id="billing-end-reason" label="Précisez">
            <Textarea id="billing-end-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={500} data-testid="billing-end-reason" />
          </Row>
        </div>
        <Footer req={req} onCancel={() => onOpenChange(false)} onSubmit={submit} label="Terminer" danger testid="billing-end-submit"
          disabled={reason.trim().length < 3 || !day} />
      </DialogContent>
    </Dialog>
  );
}

// Price and periodicity of a subscription (a draft: also its dates; running: the price only, from the next installment).
export function EditDialog({ sub, onOpenChange, onDone }) {
  const req = useRequest();
  const draft = sub?.status === "draft";
  const [periodicity, setPeriodicity] = useState(sub?.periodicity);
  const [price, setPrice] = useState(sub ? minorToText(sub.price_minor, sub.currency) : "");
  const [reason, setReason] = useState(sub?.price_reason || "");
  const [start, setStart] = useState(sub?.start_date || "");
  const [trialEnd, setTrialEnd] = useState(sub?.trial_end_date || "");
  const [note, setNote] = useState(sub?.note || "");
  if (!sub) return null;
  const version = { content: { currency: sub.currency, periods: sub.offer.periods } };
  const priceChanged = toMinor(price, sub.currency) !== sub.price_minor || periodicity !== sub.periodicity;
  const reasonNeeded = needsPriceReason(version, periodicity, price);
  const submit = () => {
    const body = { expected_version: sub.version, note };
    if (priceChanged) Object.assign(body, { price, ...(reasonNeeded ? { price_reason: reason.trim() } : {}) });
    if (draft) Object.assign(body, { periodicity, start_date: start, trial_end_date: trialEnd || null });
    req.run(() => api.patch(`/billing/subscriptions/${sub.id}`, body), onDone, "Abonnement modifié");
  };
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="billing-edit-dialog">
        <DialogHeader>
          <DialogTitle>{draft ? "Modifier le brouillon" : "Modifier le prix"}</DialogTitle>
          <DialogDescription>{draft ? "Tout se modifie tant que l'abonnement n'a pas démarré." : "Le nouveau prix s'applique aux prochaines échéances ; celles déjà créées ne changent pas."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {draft && (
            <Row id="billing-edit-periodicity" label="Périodicité">
              <Select value={periodicity} onValueChange={(v) => { setPeriodicity(v); const lp = listPrice(version, v); setPrice(lp == null ? "" : minorToText(lp, sub.currency)); }}>
                <SelectTrigger id="billing-edit-periodicity" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{sub.offer.periods.map((p) => <SelectItem key={p.periodicity} value={p.periodicity}>{PERIODICITY_LABELS[p.periodicity]}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
          )}
          <Row id="billing-edit-price" label={`Prix par période (${sub.currency})`}
            hint={listPrice(version, periodicity) == null ? "Offre sur devis : prix négocié." : `Prix de l'offre : ${money(listPrice(version, periodicity), sub.currency)}`}>
            <Input id="billing-edit-price" value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" className="h-10" data-testid="billing-edit-price" />
          </Row>
          {reasonNeeded && (
            <Row id="billing-edit-reason" label="Motif du prix négocié">
              <Input id="billing-edit-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} className="h-10" data-testid="billing-edit-reason" />
            </Row>
          )}
          {draft && (
            <div className="grid sm:grid-cols-2 gap-3">
              <Row id="billing-edit-start" label="Début"><DayInput id="billing-edit-start" value={start} onChange={setStart} /></Row>
              <Row id="billing-edit-trial" label="Fin de l'essai (facultatif)"><DayInput id="billing-edit-trial" value={trialEnd} onChange={setTrialEnd} /></Row>
            </div>
          )}
          <Row id="billing-edit-note" label="Note interne">
            <Textarea id="billing-edit-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000} />
          </Row>
        </div>
        <Footer req={req} onCancel={() => onOpenChange(false)} onSubmit={submit} label="Enregistrer" testid="billing-edit-submit"
          disabled={toMinor(price, sub.currency) === null || (reasonNeeded && reason.trim().length < 3)} />
      </DialogContent>
    </Dialog>
  );
}

export function ChangeOfferDialog({ sub, offers, onOpenChange, onDone }) {
  const req = useRequest();
  const sellable = (offers || []).filter((o) => o.active && o.published);
  const [offerId, setOfferId] = useState((sellable.find((o) => o.id === sub?.offer?.offer_id) || sellable[0])?.id || "");
  const offer = sellable.find((o) => o.id === offerId);
  const version = offer?.published;
  const [periodicity, setPeriodicity] = useState(version?.content.periods[0]?.periodicity || "monthly");
  const [price, setPrice] = useState("");
  const [priceReason, setPriceReason] = useState("");
  const [day, setDay] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => {
    const periods = version?.content.periods || [];
    const first = (periods.find((p) => p.periodicity === sub?.periodicity) || periods[0])?.periodicity || "monthly";
    setPeriodicity(first);
    const lp = listPrice(version, first);
    setPrice(lp == null ? "" : minorToText(lp, version?.content.currency || "XOF"));
  }, [offerId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!sub) return null;
  const currency = version?.content.currency || sub.currency;
  const reasonNeeded = needsPriceReason(version, periodicity, price);
  const submit = () => req.run(() => api.post(`/billing/subscriptions/${sub.id}/change-offer`, {
    expected_version: sub.version, offer_id: offerId, periodicity, price: price || null, ...(reasonNeeded ? { price_reason: priceReason.trim() } : {}),
    ...(day ? { effective_date: day } : {}), reason: reason.trim(),
  }), onDone, "Offre changée");
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="billing-change-dialog">
        <DialogHeader>
          <DialogTitle>Changer d'offre</DialogTitle>
          <DialogDescription>L'abonnement {sub.number} se termine la veille ; un nouvel abonnement démarre le jour choisi, sans calcul d'avoir automatique.</DialogDescription>
        </DialogHeader>
        {sellable.length === 0 ? <p className="text-sm text-slate-600">Aucune offre publiée n'est en vente.</p> : (
          <div className="space-y-3">
            <Row id="billing-change-offer" label="Nouvelle offre">
              <Select value={offerId} onValueChange={setOfferId}>
                <SelectTrigger id="billing-change-offer" className="h-10" data-testid="billing-change-offer"><SelectValue /></SelectTrigger>
                <SelectContent>{sellable.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <div className="grid sm:grid-cols-2 gap-3">
              <Row id="billing-change-periodicity" label="Périodicité">
                <Select value={periodicity} onValueChange={(v) => { setPeriodicity(v); const lp = listPrice(version, v); setPrice(lp == null ? "" : minorToText(lp, currency)); }}>
                  <SelectTrigger id="billing-change-periodicity" className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>{(version?.content.periods || []).map((p) => <SelectItem key={p.periodicity} value={p.periodicity}>{PERIODICITY_LABELS[p.periodicity]}</SelectItem>)}</SelectContent>
                </Select>
              </Row>
              <Row id="billing-change-price" label={`Prix (${currency})`}>
                <Input id="billing-change-price" value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" className="h-10" data-testid="billing-change-price" />
              </Row>
            </div>
            {reasonNeeded && (
              <Row id="billing-change-price-reason" label="Motif du prix négocié">
                <Input id="billing-change-price-reason" value={priceReason} onChange={(e) => setPriceReason(e.target.value)} className="h-10" />
              </Row>
            )}
            <Row id="billing-change-day" label="À partir du (facultatif)" hint="Vide : à la prochaine échéance de l'abonnement en cours.">
              <DayInput id="billing-change-day" value={day} onChange={setDay} testid="billing-change-day" />
            </Row>
            <Row id="billing-change-reason" label="Pourquoi ce changement ?">
              <Textarea id="billing-change-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} data-testid="billing-change-reason" />
            </Row>
          </div>
        )}
        <Footer req={req} onCancel={() => onOpenChange(false)} onSubmit={submit} label="Changer d'offre" testid="billing-change-submit"
          disabled={!offerId || reason.trim().length < 3 || (!price && listPrice(version, periodicity) == null) || (reasonNeeded && priceReason.trim().length < 3)} />
      </DialogContent>
    </Dialog>
  );
}

// --- Invoices, credit notes, payments, reminders --------------------------------------------------------------------------
export function InvoiceDialog({ installments, onOpenChange, onDone }) {
  const req = useRequest();
  const key = useRef(newKey());
  const currency = installments?.[0]?.currency || "XOF";
  const total = (installments || []).reduce((s, i) => s + i.amount_minor, 0);
  const [number, setNumber] = useState("");
  const [day, setDay] = useState(todayIso());
  const [amount, setAmount] = useState(minorToText(total, currency));
  const [files, setFiles] = useState([]);
  if (!installments?.length) return null;
  const submit = () => req.run(() => api.post("/billing/invoices", {
    number, issued_on: day, installment_ids: installments.map((i) => i.id), amount, file_ids: files.map((f) => f.id),
  }, { headers: { "Idempotency-Key": key.current } }), onDone, "Facture consignée");
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="billing-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Consigner la facture</DialogTitle>
          <DialogDescription>
            La facture émise pour {installments.length > 1 ? `${installments.length} échéances` : `l'échéance du ${formatDay(installments[0].period_start)}`} de {installments[0].pharmacy_name}.
            Une facture n'est pas un paiement : l'échéance reste due jusqu'à l'encaissement.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Row id="billing-invoice-number" label="Numéro de la facture">
              <Input id="billing-invoice-number" value={number} onChange={(e) => setNumber(e.target.value)} maxLength={40} className="h-10" data-testid="billing-invoice-number" />
            </Row>
            <Row id="billing-invoice-date" label="Date"><DayInput id="billing-invoice-date" value={day} onChange={setDay} max={todayIso()} /></Row>
          </div>
          <Row id="billing-invoice-amount" label={`Montant (${currency})`} hint={`Celui des échéances couvertes : ${money(total, currency)}.`}>
            <Input id="billing-invoice-amount" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" className="h-10" data-testid="billing-invoice-amount" />
          </Row>
          <FilesField files={files} setFiles={setFiles} />
        </div>
        <Footer req={req} onCancel={() => onOpenChange(false)} onSubmit={submit} label="Consigner" testid="billing-invoice-submit"
          disabled={!number.trim() || toMinor(amount, currency) === null} />
      </DialogContent>
    </Dialog>
  );
}

export function CreditNoteDialog({ invoice, onOpenChange, onDone }) {
  const req = useRequest();
  const key = useRef(newKey());
  const [number, setNumber] = useState("");
  const [day, setDay] = useState(todayIso());
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  if (!invoice) return null;
  const submit = () => req.run(() => api.post("/billing/credit-notes", {
    number, issued_on: day, invoice_id: invoice.id, amount, reason: reason.trim(), file_ids: files.map((f) => f.id),
  }, { headers: { "Idempotency-Key": key.current } }), onDone, "Avoir consigné");
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="billing-credit-dialog">
        <DialogHeader>
          <DialogTitle>Consigner un avoir</DialogTitle>
          <DialogDescription>Sur la facture {invoice.number} ({money(invoice.amount_minor, invoice.currency)}) : il réduit ce qui reste dû.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Row id="billing-credit-number" label="Numéro de l'avoir">
              <Input id="billing-credit-number" value={number} onChange={(e) => setNumber(e.target.value)} maxLength={40} className="h-10" data-testid="billing-credit-number" />
            </Row>
            <Row id="billing-credit-date" label="Date"><DayInput id="billing-credit-date" value={day} onChange={setDay} max={todayIso()} /></Row>
          </div>
          <Row id="billing-credit-amount" label={`Montant (${invoice.currency})`}>
            <Input id="billing-credit-amount" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" className="h-10" data-testid="billing-credit-amount" />
          </Row>
          <Row id="billing-credit-reason" label="Motif">
            <Textarea id="billing-credit-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} data-testid="billing-credit-reason" />
          </Row>
          <FilesField files={files} setFiles={setFiles} />
        </div>
        <Footer req={req} onCancel={() => onOpenChange(false)} onSubmit={submit} label="Consigner" testid="billing-credit-submit"
          disabled={!number.trim() || toMinor(amount, invoice.currency) === null || reason.trim().length < 3} />
      </DialogContent>
    </Dialog>
  );
}

// `target` = { installment } or { invoice, remaining_minor }: what the payment settles.
export function PaymentDialog({ target, onOpenChange, onDone }) {
  const req = useRequest();
  const key = useRef(newKey());
  const item = target?.installment || target?.invoice;
  const currency = item?.currency || "XOF";
  const due = target?.installment ? target.installment.remaining_minor : target?.remaining_minor;
  const [amount, setAmount] = useState(due ? minorToText(due, currency) : "");
  const [method, setMethod] = useState("transfer");
  const [day, setDay] = useState(todayIso());
  const [reference, setReference] = useState("");
  const [files, setFiles] = useState([]);
  if (!item) return null;
  const submit = () => req.run(() => api.post("/billing/payments", {
    ...(target.installment ? { installment_id: target.installment.id } : { invoice_id: target.invoice.id }),
    amount, method, paid_on: day, reference, file_ids: files.map((f) => f.id),
  }, { headers: { "Idempotency-Key": key.current } }), onDone, "Encaissement consigné");
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="billing-payment-dialog">
        <DialogHeader>
          <DialogTitle>Consigner un encaissement</DialogTitle>
          <DialogDescription>
            {target.invoice ? `Facture ${target.invoice.number}` : `Échéance du ${formatDay(item.period_start)}`} · {item.pharmacy_name} · reste dû {money(due, currency)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Row id="billing-payment-amount" label={`Montant reçu (${currency})`} hint="Un paiement partiel est possible.">
              <Input id="billing-payment-amount" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" className="h-10" data-testid="billing-payment-amount" />
            </Row>
            <Row id="billing-payment-date" label="Reçu le"><DayInput id="billing-payment-date" value={day} onChange={setDay} max={todayIso()} /></Row>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Row id="billing-payment-method" label="Moyen">
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger id="billing-payment-method" className="h-10" data-testid="billing-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PAYMENT_METHOD_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row id="billing-payment-reference" label="Référence (facultatif)">
              <Input id="billing-payment-reference" value={reference} onChange={(e) => setReference(e.target.value)} maxLength={120} className="h-10" placeholder="N° de transaction, de chèque…" />
            </Row>
          </div>
          <FilesField files={files} setFiles={setFiles} />
        </div>
        <Footer req={req} onCancel={() => onOpenChange(false)} onSubmit={submit} label="Consigner" testid="billing-payment-submit"
          disabled={toMinor(amount, currency) === null} />
      </DialogContent>
    </Dialog>
  );
}

export function RefundDialog({ payment, onOpenChange, onDone }) {
  const req = useRequest();
  const [amount, setAmount] = useState("");
  const [day, setDay] = useState(todayIso());
  const [reason, setReason] = useState("");
  if (!payment) return null;
  const submit = () => req.run(() => api.post(`/billing/payments/${payment.id}/refund`,
    { expected_version: payment.version, amount, refunded_on: day, reason: reason.trim() }), onDone, "Remboursement consigné");
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="billing-refund-dialog">
        <DialogHeader>
          <DialogTitle>Consigner un remboursement</DialogTitle>
          <DialogDescription>De l'encaissement du {formatDay(payment.paid_on)} ({money(payment.net_minor, payment.currency)}). Les échéances couvertes redeviennent dues d'autant.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Row id="billing-refund-amount" label={`Montant rendu (${payment.currency})`}>
              <Input id="billing-refund-amount" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" className="h-10" data-testid="billing-refund-amount" />
            </Row>
            <Row id="billing-refund-date" label="Rendu le"><DayInput id="billing-refund-date" value={day} onChange={setDay} max={todayIso()} /></Row>
          </div>
          <Row id="billing-refund-reason" label="Motif">
            <Textarea id="billing-refund-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} data-testid="billing-refund-reason" />
          </Row>
        </div>
        <Footer req={req} onCancel={() => onOpenChange(false)} onSubmit={submit} label="Consigner" testid="billing-refund-submit"
          disabled={toMinor(amount, payment.currency) === null || reason.trim().length < 3} />
      </DialogContent>
    </Dialog>
  );
}

export function ReminderDialog({ installment, onOpenChange, onDone }) {
  const req = useRequest();
  const [channel, setChannel] = useState("phone");
  const [day, setDay] = useState(todayIso());
  const [note, setNote] = useState("");
  if (!installment) return null;
  const submit = () => req.run(() => api.post(`/billing/installments/${installment.id}/reminders`, { channel, reminded_on: day, note }), onDone,
    "Relance consignée");
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent data-testid="billing-reminder-dialog">
        <DialogHeader>
          <DialogTitle>Consigner une relance</DialogTitle>
          <DialogDescription>La relance que vous avez faite pour l'échéance du {formatDay(installment.period_start)} de {installment.pharmacy_name}. Le CRM n'envoie rien à la pharmacie.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Row id="billing-reminder-channel" label="Comment">
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger id="billing-reminder-channel" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(REMINDER_CHANNEL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row id="billing-reminder-date" label="Quand"><DayInput id="billing-reminder-date" value={day} onChange={setDay} max={todayIso()} /></Row>
          </div>
          <Row id="billing-reminder-note" label="Ce qui a été convenu (facultatif)">
            <Textarea id="billing-reminder-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000} data-testid="billing-reminder-note" />
          </Row>
        </div>
        <Footer req={req} onCancel={() => onOpenChange(false)} onSubmit={submit} label="Consigner" testid="billing-reminder-submit" />
      </DialogContent>
    </Dialog>
  );
}
